import React, { useState, useEffect } from "react";
import { motion, useScroll, useMotionValueEvent } from "framer-motion";
import Logo from "../assets/icons/Logo";
import NavBarMobile from "./NavBarMobile";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import { Link, useLocation } from "react-router-dom";
import CustomButton from "./CustomButton";

export const links = [
  // {
  //   href: "/#home",
  //   name: "Home",
  //   spy: "home",
  // },
  {
    href: "/#innovative",
    name: "Innovative",
    spy: "innovative",
  },
  {
    href: "/solutions",
    name: "Solutions",
    spy: "solutions",
  },

  {
    href: "/#projects",
    name: "Projects",
    spy: "projects",
  },
  {
    href: "/#faq",
    name: "FAQ",
    spy: "faq",
  },
  // {
  //   href: "/#team",
  //   name: "Team",
  //   spy: "team",
  // },
  {
    href: "/pricing",
    name: "Pricing",
    spy: "pricing",
  },
  {
    href: "https://www.linkedin.com/company/drospect/jobs/",
    name: "Careers",
    target: "_blank",
  },
];
const Navbar = ({ isFooter }) => {
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isSolutionsMenuOpen, setIsSolutionsMenuOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [activeHomeSection, setActiveHomeSection] = useState("");
  const { scrollY } = useScroll();
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isSolutionDetailRoute = /^\/solutions\/[^/]+$/.test(location.pathname);
  const solutionsSubLinks = [
    {
      name: "Thermal Inspection",
      href: "/solutions/thermal-orthomosaic",
    },
    {
      name: "Defect Detection",
      href: "/solutions/defect-hotspot-detection",
    },
    {
      name: "Satellite Imagery Inspections",
      href: "/solutions/performance-screening",
    },
    {
      name: "API & Integrations",
      href: "/solutions/data-integration",
    },
  ];

  // Check initial scroll position
  useEffect(() => {
    const checkScroll = () => {
      const currentScroll = window.scrollY || window.pageYOffset;
      setHasScrolled(currentScroll > 10);
    };
    checkScroll();
    window.addEventListener("scroll", checkScroll, { passive: true });
    return () => window.removeEventListener("scroll", checkScroll);
  }, []);

  useMotionValueEvent(scrollY, "change", (latest) => {
    if (latest > lastScrollY && latest > 100) {
      // Scrolling down
      setIsVisible(false);
    } else if (latest < lastScrollY) {
      // Scrolling up
      setIsVisible(true);
    }
    // Make navbar transparent when at or near the top (within 10px)
    setHasScrolled(latest > 10);
    setLastScrollY(latest);
  });

  useEffect(() => {
    setIsSolutionsMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/") {
      setActiveHomeSection("");
      return;
    }

    const homeSectionIds = ["innovative", "projects", "faq"];
    const resolveActiveSection = () => {
      const navLine = 130;
      let currentSection = "";
      const sections = homeSectionIds
        .map((id) => {
          const node = document.getElementById(id);
          if (!node) return null;
          const sectionNode =
            node.tagName.toLowerCase() === "section"
              ? node
              : node.closest("section") || node;
          return { id, node: sectionNode };
        })
        .filter(Boolean);

      for (const section of sections) {
        const rect = section.node.getBoundingClientRect();
        if (rect.top <= navLine && rect.bottom > navLine) {
          currentSection = section.id;
          break;
        }
      }

      if (!currentSection) {
        const passedSections = sections
          .filter(
            (section) => section.node.getBoundingClientRect().top <= navLine,
          )
          .map((section) => section.id);

        if (passedSections.length > 0) {
          currentSection = passedSections[passedSections.length - 1];
        }
      }

      if (!currentSection && location.hash) {
        const hashSection = location.hash.replace("#", "");
        if (homeSectionIds.includes(hashSection)) {
          currentSection = hashSection;
        }
      }

      setActiveHomeSection(currentSection);
    };

    resolveActiveSection();
    window.addEventListener("scroll", resolveActiveSection, { passive: true });
    window.addEventListener("resize", resolveActiveSection);

    return () => {
      window.removeEventListener("scroll", resolveActiveSection);
      window.removeEventListener("resize", resolveActiveSection);
    };
  }, [location.pathname, location.hash]);

  return (
    <>
      <motion.nav
        className={`${
          isHome ? "fixed" : "sticky"
        } z-50 top-0 w-full overflow-visible mx-auto py-4 md:px-16 px-4 ${
          hasScrolled && !isSolutionDetailRoute && isHome
            ? "backdrop-blur-md"
            : ""
        }`}
        initial={{ y: 0, backgroundColor: "rgba(0,0,0,0)" }}
        animate={{
          y: isVisible ? 0 : -100,
          backgroundColor:
            hasScrolled || isSolutionDetailRoute
              ? "rgba(0,0,0,0.6)"
              : "rgba(0,0,0,0)",
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <div className="relative z-[110] flex justify-between w-full items-center max-w-[1440px] mx-auto ">
          <div className="logo w-fit">
            <Link
              to="/"
              aria-label="Go to homepage"
              onClick={() => {
                setIsNavOpen(false);
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
            >
              <Logo width="130" height="30" />
            </Link>
          </div>
          <div
            className={` xl:justify-end items-center w-fit gap-6 md:gap-10 font-nulshock font-normal text-base hidden min-[1070px]:flex relative  ${
              isFooter ? "text-[#656565]" : "text-white "
            } `}
          >
            {links.map((link) => {
              const isActive =
                (location.pathname.startsWith("/solutions") &&
                  link.href === "/solutions") ||
                (location.pathname === "/pricing" &&
                  link.href === "/pricing") ||
                (location.pathname === "/" &&
                  Boolean(link.spy) &&
                  link.spy === activeHomeSection);
              const isSolutionsLink = link.href === "/solutions";

              return (
                <div
                  key={link.name}
                  className="relative group"
                  onMouseEnter={() => {
                    if (isSolutionsLink) setIsSolutionsMenuOpen(true);
                  }}
                  onMouseLeave={() => {
                    if (isSolutionsLink) setIsSolutionsMenuOpen(false);
                  }}
                >
                  <CustomButton
                    to={link.href}
                    variant="link"
                    data-to-scrollspy-id={link.spy}
                    target={link?.target}
                    rel={link?.rel}
                    onClick={() => {
                      if (location.pathname === "/" && link.spy) {
                        setActiveHomeSection(link.spy);
                      }
                    }}
                    className={`
                    inline-flex items-center h-8 leading-none
                    relative after:absolute after:left-0 after:bottom-[-4px] after:h-[2px] 
                    after:bg-current after:transition-all after:duration-300
                    hover:after:w-full
                    ${isActive ? "after:w-full" : "after:w-0"}
                  `}
                  >
                    {link.name}
                    {isSolutionsLink && (
                      <motion.span
                        className="inline-flex items-center ml-1"
                        animate={{ rotate: isSolutionsMenuOpen ? 180 : 0 }}
                        transition={{ duration: 0.25, ease: "easeInOut" }}
                      >
                        <KeyboardArrowDownIcon fontSize="small" />
                      </motion.span>
                    )}
                  </CustomButton>

                  {isSolutionsLink && (
                    <div
                      className={`absolute -translate-x-1/2 left-1/2 top-full pt-2 z-50 ${
                        isSolutionsMenuOpen ? "block" : "hidden"
                      }`}
                    >
                      <div
                        className={`w-fit rounded-xl text-white shadow-xl py-2 ${
                          hasScrolled
                            ? "border backdrop-blur-2xl bg-black border-white/25"
                            : "backdrop-blur-md bg-white/15 border-white/30"
                        }`}
                      >
                        {solutionsSubLinks.map((subLink) => (
                          <CustomButton
                            key={subLink.name}
                            to={subLink.href}
                            variant="link"
                            onClick={() => setIsSolutionsMenuOpen(false)}
                            className="block !justify-start w-full px-4 py-2 text-left text-sm !text-white hover:!text-white hover:bg-white/15"
                          >
                            {subLink.name}
                          </CustomButton>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="hidden min-[1070px]:block">
            <CustomButton
              children={"Book a Demo"}
              to="/contact-us"
              variant="outlined"
              size="md"
            />
          </div>

          {/* Hamburger Menu Button for Mobile */}
          <button
            className="min-[1070px]:hidden flex flex-col justify-center items-center w-10 h-10 space-y-1.5 z-50"
            onClick={() => setIsNavOpen(!isNavOpen)}
            aria-label="Toggle menu"
          >
            <motion.span
              animate={isNavOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
              className="block w-6 h-0.5 bg-white"
            />
            <motion.span
              animate={isNavOpen ? { opacity: 0 } : { opacity: 1 }}
              className="block w-6 h-0.5 bg-white"
            />
            <motion.span
              animate={isNavOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
              className="block w-6 h-0.5 bg-white"
            />
          </button>
        </div>
      </motion.nav>

      {/* Mobile Navigation Menu */}
      <NavBarMobile
        isNavOpen={isNavOpen}
        setIsNavOpen={setIsNavOpen}
        location={location}
      />
    </>
  );
};

export default Navbar;
